import get from "lodash/get";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import StatCard from "../../../parts/cards/StatCard";
import ToggleLinks from "../../../parts/inputs/toggle-links-input";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { formatNumber } from "../../../utils/format-sub";
import StatBand from "../income/components/StatBand";
import PassesTable from "./components/PassesTable";
import SubscriberDetails from "./components/SubscriberDetails";
import SubscribersTable from "./components/SubscribersTable";

// This component is used for the subscriber list as well
const MembershipList = ({
  csvString,
  allSubs,
  subscribers,
  productType = "membership",
  api,
}) => {
  const { locals } = useRillContext();

  const [all, setAll] = useState({ activeSubs: [], churnedSubs: [] });
  const [active, setActive] = useState({
    currentSubs: [],
    currentPage: null,
    totalPages: null,
  });
  const [churned, setChurned] = useState({
    currentSubs: [],
    currentPage: null,
    totalPages: null,
  });

  const [sortConfig, setSortConfigState] = useState({
    key: "customer_name",
    direction: "ascending",
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const { t } = useTranslation("coaching-portal");
  const userStr =
    productType === "membership"
      ? t("membershipList.members")
      : t("membershipList.subscribers");

  useEffect(() => {
    const { activeSubs, churnedSubs } = splitActiveList(subscribers);
    setAll({ activeSubs, churnedSubs });
  }, [subscribers]);

  const setSortConfig = ({ target }, direction = "ascending") => {
    setSortConfigState({ key: target.value, direction });
  };

  const sortTable = (
    subscribers,
    sortConfig,
    search,
    statusFilter,
    planFilter
  ) => {
    let sortedSubscribers = [...subscribers];
    if (search) {
      sortedSubscribers = sortedSubscribers.filter((sub) => {
        return (
          String(sub.customer_name)
            .toLowerCase()
            .includes(search.toLowerCase().trim()) ||
          String(sub.customer_email)
            .toLowerCase()
            .includes(search.toLowerCase().trim()) ||
          String(sub.phoneNumber)
            .toLowerCase()
            .includes(search.toLowerCase().trim()) ||
          String(sub.plan.interval)
            .toLowerCase()
            .includes(search.toLowerCase().trim())
        );
      });
    }
    if (statusFilter) {
      sortedSubscribers = sortedSubscribers.filter(
        (sub) => sub.paymentStatus === statusFilter
      );
    }
    if (planFilter) {
      sortedSubscribers = sortedSubscribers.filter(
        (sub) => sub.plan.id === planFilter
      );
    }
    if (sortConfig !== null) {
      sortedSubscribers.sort((a, b) => {
        if (get(a, sortConfig.key) < get(b, sortConfig.key)) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (get(a, sortConfig.key) > get(b, sortConfig.key)) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedSubscribers;
  };

  const onActivePageChanged = (data) => {
    const { activeSubs } = all;
    const { currentPage, totalPages, pageLimit } = data;
    const offset = (currentPage - 1) * pageLimit;
    const currentActiveSubs = activeSubs.slice(offset, offset + pageLimit);

    setActive({
      currentPage,
      currentSubs: currentActiveSubs,
      totalPages,
    });
  };

  const onChurnedPageChanged = (data) => {
    const { churnedSubs } = all;
    const { currentPage, totalPages, pageLimit } = data;
    const offset = (currentPage - 1) * pageLimit;
    const currentChurnedSubs = churnedSubs.slice(offset, offset + pageLimit);

    setChurned({ currentPage, currentSubs: currentChurnedSubs, totalPages });
  };

  const splitActiveList = (subscribers) => {
    const splitList = {
      activeSubs: subscribers.filter((sub) => sub.isActive),
      churnedSubs: subscribers.filter((sub) => !sub.isActive),
    };
    return splitList;
  };

  const { query, subscriberInvoices, upcomingInvoice } = locals;

  const [currentSubs, type] = useMemo(() => {
    let currentSubs = active.currentSubs;
    let type = "active";
    if (query?.type === "churned") {
      type = "churned";
      currentSubs = churned.currentSubs;
    }
    return [
      sortTable(currentSubs, sortConfig, search, statusFilter, planFilter),
      type,
    ];
  }, [active, churned, query, sortConfig, search, statusFilter, planFilter]);

  // Unique plans across active+churned, for the plan filter dropdown —
  // built from whatever this coach has actually sold, not the full price
  // catalog, so an unused plan doesn't clutter the list.
  const planOptions = useMemo(() => {
    const seen = new Map();
    [...all.activeSubs, ...all.churnedSubs].forEach((sub) => {
      if (sub.plan?.id && !seen.has(sub.plan.id)) {
        seen.set(sub.plan.id, sub.plan.interval);
      }
    });
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [all]);

  const toggleDrawer = (e, customer = "", price = "") => {
    e.preventDefault();
    submit("/actions/refresh", {
      method: "GET",
      body: {
        customer,
        price,
      },
    });
  };

  const cancelSubscription = (e) => {
    e.preventDefault();
    if (confirm(t("membershipList.cancelConfirm", { productType }))) {
      submit("/actions/user/cancel-subscriber", {
        method: "POST",
        body: {
          customer: query.customer,
          type: productType,
        },
      });
    }
  };

  const showPasses = productType === "membership" && query?.view === "passes";

  return (
    <main className="subscribers-list">
      {productType === "membership" && (
        <div className="view-toggle mb-4">
          <ToggleLinks
            name="view"
            options={[
              { text: t("membershipList.membersTab"), value: "members" },
              {
                text: t("membershipList.passesTable.tabLabel"),
                value: "passes",
              },
            ]}
            defaultActive="members"
          />
        </div>
      )}

      {!showPasses && (
        <>
          {productType === "membership" && (
            <>
              <StatBand
                kind="snapshot"
                asAt={allSubs.newestSynced}
                title={t("membershipList.snapshotBandTitle")}
                t={t}
              >
                <StatCard
                  title={t("membershipList.totalMembers", { type: userStr })}
                  tooltipText={t("membershipList.totalTooltip", {
                    type: userStr,
                  })}
                  current={allSubs.totalSubscribers}
                  previous={allSubs.chartData.totalSubscribersArray[0]}
                  // chartData.*Array is newest-first (matches msb-mobile's
                  // `[0]` "last month" read above); Sparkline renders array
                  // order left-to-right, so a display-only reversed copy
                  // restores chronological (oldest->newest) bars without
                  // touching the newest-first array `previous` relies on.
                  sparklineData={[
                    ...allSubs.chartData.totalSubscribersArray,
                  ].reverse()}
                  sparklineLabels={[...allSubs.chartData.monthsArray].reverse()}
                />
                <StatCard
                  title={t("membershipList.onPass", { type: userStr })}
                  tooltipText={t("membershipList.onPassTooltip")}
                  current={all.activeSubs.filter((s) => s.isPass).length}
                />
                <StatCard
                  title={t("membershipList.paymentIssues")}
                  tooltipText={t("membershipList.paymentIssuesTooltip")}
                  current={all.activeSubs.filter((s) => s.retryStatus).length}
                />
              </StatBand>
              <StatBand
                kind="period"
                title={t("membershipList.periodBandTitle")}
                rangeLabel={t("membershipList.trailingMonth")}
                t={t}
              >
                <StatCard
                  title={t("membershipList.newMembers", { type: userStr })}
                  tooltipText={t("membershipList.newTooltip", {
                    type: userStr,
                  })}
                  current={allSubs.newSubscribers}
                  caption={t("membershipList.lastMonthCaption", {
                    count: allSubs.chartData.newSubscribersArray[0],
                  })}
                  sparklineData={[
                    ...allSubs.chartData.newSubscribersArray,
                  ].reverse()}
                  sparklineLabels={[...allSubs.chartData.monthsArray].reverse()}
                />
                <StatCard
                  title={t("membershipList.churnedMembers", {
                    type: userStr,
                  })}
                  tooltipText={t("membershipList.churnedTooltip", {
                    type: userStr,
                  })}
                  current={allSubs.churnedSubscribers}
                  caption={t("membershipList.lastMonthCaption", {
                    count: allSubs.chartData.churnedSubscribersArray[0],
                  })}
                  sparklineData={[
                    ...allSubs.chartData.churnedSubscribersArray,
                  ].reverse()}
                  sparklineLabels={[...allSubs.chartData.monthsArray].reverse()}
                />
                <StatCard
                  title={t("membershipList.revenuePerMember")}
                  tooltipText={t("membershipList.revenuePerMemberTooltip")}
                  current={allSubs.revenuePerMember || 0}
                  formatValue={(v) =>
                    formatNumber(Number(v), allSubs.accountCurrency || "usd")
                  }
                />
              </StatBand>
            </>
          )}

          {productType === "subscription" && (
            <StatBand
              kind="snapshot"
              asAt={allSubs.newestSynced}
              title={t("membershipList.snapshotBandTitle")}
              t={t}
            >
              <StatCard
                title={t("membershipList.totalMembers", { type: userStr })}
                tooltipText={t("membershipList.totalTooltip", {
                  type: userStr,
                })}
                current={allSubs.totalSubscribers}
                previous={allSubs.chartData.totalSubscribersArray[0]}
                sparklineData={[
                  ...allSubs.chartData.totalSubscribersArray,
                ].reverse()}
                sparklineLabels={[...allSubs.chartData.monthsArray].reverse()}
              />
              <StatCard
                title={t("membershipList.onFreeTrial")}
                tooltipText={t("membershipList.onFreeTrialTooltip")}
                current={
                  all.activeSubs.filter((s) => s.status === "trialing").length
                }
              />
              <StatCard
                title={t("membershipList.onCoupon")}
                tooltipText={t("membershipList.onCouponTooltip")}
                current={
                  all.activeSubs.filter((s) => s.source?.flags.coupon).length
                }
              />
              <StatCard
                title={t("membershipList.cameViaReferral")}
                tooltipText={t("membershipList.cameViaReferralTooltip")}
                current={
                  all.activeSubs.filter((s) => s.source?.flags.referred).length
                }
              />
            </StatBand>
          )}

          {productType === "subscription" && (
            <p className="p2 mt-2">
              {t("membershipList.sourcePrecedenceNote")}
            </p>
          )}

          {productType === "subscription" && (
            <StatBand
              kind="period"
              title={t("membershipList.periodBandTitle")}
              rangeLabel={t("membershipList.trailingMonth")}
              t={t}
            >
              <StatCard
                title={t("membershipList.newMembers", { type: userStr })}
                tooltipText={t("membershipList.newTooltip", { type: userStr })}
                current={allSubs.newSubscribers}
                caption={t("membershipList.lastMonthCaption", {
                  count: allSubs.chartData.newSubscribersArray[0],
                })}
                sparklineData={[
                  ...allSubs.chartData.newSubscribersArray,
                ].reverse()}
                sparklineLabels={[...allSubs.chartData.monthsArray].reverse()}
              />
              <StatCard
                title={t("membershipList.churnedMembers", { type: userStr })}
                tooltipText={t("membershipList.churnedTooltip", {
                  type: userStr,
                })}
                current={allSubs.churnedSubscribers}
                caption={t("membershipList.lastMonthCaption", {
                  count: allSubs.chartData.churnedSubscribersArray[0],
                })}
                sparklineData={[
                  ...allSubs.chartData.churnedSubscribersArray,
                ].reverse()}
                sparklineLabels={[...allSubs.chartData.monthsArray].reverse()}
              />
            </StatBand>
          )}

          <SubscribersTable
            tableTitle={
              productType === "membership"
                ? t("membershipList.memberList")
                : t("membershipList.subscriberList")
            }
            sortConfig={sortConfig}
            handleSort={setSortConfig}
            subscribers={currentSubs}
            type={type}
            allActiveSubs={all.activeSubs}
            allChurnedSubs={all.churnedSubs}
            onActivePageChanged={onActivePageChanged}
            onChurnedPageChanged={onChurnedPageChanged}
            csvString={csvString}
            nameString={
              productType === "membership"
                ? t("membershipList.member")
                : t("membershipList.subscriber")
            }
            onDetailsClick={toggleDrawer}
            search={search}
            setSearch={setSearch}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            planFilter={planFilter}
            setPlanFilter={setPlanFilter}
            planOptions={planOptions}
            productType={productType}
          />
        </>
      )}

      {showPasses && (
        <PassesTable
          passes={allSubs.passes}
          currency={allSubs.accountCurrency}
        />
      )}

      <SubscriberDetails
        subscriptions={subscribers}
        customer={query?.customer}
        toggleDrawer={toggleDrawer}
        handleCancel={cancelSubscription}
        type={productType}
        subscriberInvoices={subscriberInvoices}
        price={query?.price}
        upcomingInvoice={upcomingInvoice}
        api={api}
      />
    </main>
  );
};

export default MembershipList;
